import { AccountAuthModel } from '$store/models';
import WebApp from '@twa-dev/sdk';
import {
  API_URL,
  FORCE_GMAIL_OAUTH,
  GOOGLE_CLIENT_ID,
  TELEGRAM_BOT_ID,
} from '$shared/constants';

export function setStorageItem(name: string, value: string): void {
  try {
    localStorage.setItem(name, value);
  } catch (e) {}
}

export function getStorageItem(name: string): string | null | undefined {
  try {
    return localStorage.getItem(name);
  } catch (e) {}
}

export function removeStorageItem(name: string): void {
  try {
    localStorage.removeItem(name);
  } catch (e) {}
}

export const getToken = () => {
  if (window.isWebApp) {
    const data = WebApp.initDataUnsafe;
    const tgData = Object.keys(data)
      .filter((key) => key !== 'hash')
      .sort()
      .map(
        (k) => `${k}=${typeof data[k] === 'object' ? JSON.stringify(data[k]) : data[k]}`,
      )
      .join('\n');

    return {
      token: `TgAuthNative ${btoa(
        [unescape(encodeURIComponent(tgData)), data.hash].join(';'),
      )}`,
      userId: 1,
    };
  }

  const raw = (localStorage.getItem('token') || '').split(',');
  return {
    userId: +raw[0],
    token: raw[1],
  };
};

export const setTokenRaw = (token: string): void => {
  localStorage.setItem('token', token);
};

export const getTokenRaw = (): string | null => {
  return localStorage.getItem('token');
};

export const setToken = (userId: number, token: string) => {
  localStorage.setItem('token', `${userId},${token}`);
};

export const changeAccount = (id: number) => {
  if (window.isWebApp) {
    return;
  }

  const currentAccount = getToken();
  let containCurrentAccount = false;
  const accounts = getAccounts().map((account) => {
    id === account.id && setToken(id, account.token);
    if (currentAccount.userId === account.id) {
      containCurrentAccount = true;
    }
    return {
      ...account,
      updatedAt: id === account.id ? Date.now() : account.updatedAt,
    };
  });

  if (!containCurrentAccount) {
    accounts.unshift({
      id: currentAccount.userId,
      updatedAt: Date.now(),
      token: currentAccount.token,
    });
  }

  setAccounts(accounts);
};

export const setAccounts = (account: AccountAuthModel[]) => {
  localStorage.setItem('accounts', JSON.stringify(account));
};

export const addAccount = (account: AccountAuthModel) => {
  if (window.isWebApp) {
    return;
  }
  const accounts = getAccounts();
  setAccounts([account, ...accounts.filter((a) => a.id !== account.id)]);
};

export const removeAccount = (userId: number) => {
  const accounts = getAccounts();
  setAccounts(accounts.filter((a) => a.id !== userId));
};

export const getAccounts = (): AccountAuthModel[] => {
  if (window.isWebApp) {
    return [];
  }

  const accountsString = localStorage.getItem('accounts');
  if (!accountsString) {
    return [];
  }
  try {
    return JSON.parse(accountsString);
  } catch (e) {
    return [];
  }
};

export const removeToken = () => {
  localStorage.removeItem('token');
};

let cachedApiUrl: string | null = null;

export const getApiUrl = (): string => {
  if (!cachedApiUrl) {
    cachedApiUrl = localStorage.getItem('apiUrl') || API_URL;
  }

  return cachedApiUrl;
};

export const setApiUrl = (url: string): void => {
  cachedApiUrl = url;
  localStorage.setItem('apiUrl', url);
};

export const formatUrl = (
  baseUrl: string,
  path: string,
  searchParams?: Record<string, string | number | boolean>,
): string => {
  const url = new URL(path, baseUrl);

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
};

export const formatApiUrl = (
  path: string,
  searchParams?: Record<string, string | number | boolean>,
): string => {
  return formatUrl(getApiUrl(), path, searchParams);
};

export const setRegistrationToken = (token: string) => {
  localStorage.setItem('registration_token', token);
};

export const removeRegistrationToken = () => {
  localStorage.removeItem('registration_token');
};

export const getRegistrationToken = (): string | null => {
  return localStorage.getItem('registration_token');
};

export const setTargetModal = (name: string) => {
  setStorageItem('target_modal', name);
};

export const removeTargetModal = () => removeStorageItem('target_modal');

export const getTargetModal = (clear?: boolean) => {
  const result = getStorageItem('target_modal');
  if (result && clear) {
    removeTargetModal();
  }
  return result;
};

let cachedForceGmailOAuth: boolean | undefined = undefined;

export const getForceGmailOAuth = () => {
  if (cachedForceGmailOAuth === undefined) {
    cachedForceGmailOAuth = ['y', '1', 'yes', true, 1].includes(
      localStorage.getItem('force_gmail_oauth') || FORCE_GMAIL_OAUTH,
    );
  }

  return cachedForceGmailOAuth;
};

export const setForceGmailOAuth = (value: boolean) => {
  cachedForceGmailOAuth = value;
  localStorage.setItem('force_gmail_oauth', value ? 'true' : 'false');
};

let cachedTelegramBotId: number | null = null;

export const getTelegramBotId = () => {
  if (!cachedTelegramBotId) {
    cachedTelegramBotId = parseInt(
      localStorage.getItem('telegram_auth_bot_id') || TELEGRAM_BOT_ID,
    );
  }

  return cachedTelegramBotId;
};

export const setTelegramBotId = (value: number) => {
  cachedTelegramBotId = value;
  localStorage.setItem('telegram_auth_bot_id', value.toString());
};

let cachedGoogleClientId: string | null = null;

export const getGoogleClientId = () => {
  if (!cachedGoogleClientId) {
    cachedGoogleClientId =
      localStorage.getItem('google_auth_client_id') || GOOGLE_CLIENT_ID;
  }

  return cachedGoogleClientId;
};

export const setGoogleClientId = (value: string) => {
  cachedGoogleClientId = value;
  localStorage.setItem('google_auth_client_id', value);
};

let OnlyPayPopUpShown = false;

export function getOnlyPayPopUpShown(): boolean {
  return OnlyPayPopUpShown;
}

export function setOnlyPayPopUpShown(value: boolean): void {
  OnlyPayPopUpShown = value;
}
